import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { useTranslation } from '../i18n';
import './PracticeCalendar.css';

interface PracticeCalendarProps {
  className?: string;
}

export const PracticeCalendar: React.FC<PracticeCalendarProps> = ({ className = '' }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [practiceDays, setPracticeDays] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const t = useTranslation();

  useEffect(() => {
    loadPracticeDays();
  }, [currentDate]);

  const loadPracticeDays = async () => {
    try {
      setLoading(true);
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      
      const response = await apiClient.getPracticeDays(year, month);
      setPracticeDays(response.data.practice_days || []);
    } catch (error) {
      console.error('Failed to load practice days:', error);
      setPracticeDays([]);
    } finally {
      setLoading(false);
    }
  };

  const isPracticeDay = (date: Date): boolean => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    return practiceDays.includes(dateString);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getMonthName = (date: Date): string => {
    return t.calendar.months[date.getMonth()];
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days: Date[] = [];
    
    // Add empty cells for days before the first day of the month
    const firstDayOfWeek = firstDay.getDay();
    const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Monday as first day
    
    for (let i = 0; i < startOffset; i++) {
      days.push(new Date(year, month, -startOffset + i + 1));
    }
    
    // Add all days of the month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    // Add empty cells for days after the last day of the month to complete the grid
    const remainingDays = 7 - (days.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        days.push(new Date(year, month + 1, i));
      }
    }
    
    return days;
  };

  const days = getDaysInMonth(currentDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <div className={`practice-calendar ${className}`}>
      <div className="calendar-header">
        <button 
          className="calendar-nav-btn" 
          onClick={goToPreviousMonth}
          aria-label={t.calendar.previousMonth}
        >
          ‹
        </button>
        <h3 className="calendar-title">
          {getMonthName(currentDate)} {currentDate.getFullYear()}
        </h3>
        <button 
          className="calendar-nav-btn" 
          onClick={goToNextMonth}
          aria-label={t.calendar.nextMonth}
        >
          ›
        </button>
      </div>

      <div className="calendar-weekdays">
        {t.calendar.weekdays.map((day, index) => (
          <div key={index} className="calendar-weekday">{day}</div>
        ))}
      </div>

      {loading ? (
        <div className="calendar-loading">
          <div className="calendar-spinner"></div>
        </div>
      ) : (
        <div className="calendar-grid">
          {days.map((day, index) => {
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();
            const isToday = day.getTime() === today.getTime();
            const hasPractice = isCurrentMonth && isPracticeDay(day);
            
            return (
              <div
                key={index}
                className={`
                  calendar-day
                  ${!isCurrentMonth ? 'calendar-day--other-month' : ''}
                  ${isToday ? 'calendar-day--today' : ''}
                  ${hasPractice ? 'calendar-day--practice' : ''}
                `}
              >
                <span className="calendar-day-number">{day.getDate()}</span>
              </div>
            );
          })}
        </div>
      )}

      <div className="calendar-legend">
        <div className="calendar-legend-item">
          <div className="calendar-legend-color calendar-legend-color--practice"></div>
          <span className="calendar-legend-text">{t.calendar.practiceDay}</span>
        </div>
      </div>
    </div>
  );
};
